import apiClient from "./apiClient";
import { saveResponseAsFile } from "../utils/downloadFile";
import {
    KmzImportResult, ProjectChartImageType, TimeLocationChartData, TowerCoordinateStatus
} from "../types/timeLocationChartTypes";

/**
 * Obtiene los datos del Diagrama Espacio-Tiempo.
 * El backend entrega las progresivas calculadas y los puntos ya ordenados.
 */
export const getTimeLocationChart = async (
    projectId: number,
    activityIds: number[],
    startDateActivityIds: number[],
    dateFrom?: string,
    dateTo?: string
): Promise<TimeLocationChartData> => {
    const response = await apiClient.get('/TimeLocationChart', {
        params: {
            projectId,
            activityIds: activityIds.join(','),
            // Las que no van en esta lista se grafican por fecha de término.
            ...(startDateActivityIds.length > 0 && { startDateActivityIds: startDateActivityIds.join(',') }),
            ...(dateFrom && { dateFrom }),
            ...(dateTo && { dateTo })
        }
    });
    return response.data;
};

/** Capas de contexto que el proyecto ya tiene cargadas. */
export const getAvailableChartImages = async (projectId: number): Promise<ProjectChartImageType[]> => {
    try {
        const response = await apiClient.get(`/ProjectChartImages/${projectId}`);
        return response.data;
    } catch (error) {
        console.error('Error al obtener las imágenes del diagrama:', error);
        return [];
    }
};

/**
 * Descarga una imagen de contexto y devuelve una URL de objeto local.
 *
 * No se puede apuntar el src de la imagen directamente al endpoint: el token JWT se adjunta
 * en el interceptor de apiClient, y una etiqueta img hace su propia petición sin pasar por
 * axios, así que el backend la rechazaría por falta de autenticación.
 *
 * Descargar el binario por apiClient resuelve además la exportación: un blob URL es del
 * mismo origen, con lo que html2canvas puede volcar el canvas sin que quede bloqueado por
 * CORS.
 *
 * Quien llame a esta función es responsable de liberar la URL con revokeObjectURL.
 *
 * @returns la URL de objeto, o null si el proyecto no tiene esa imagen cargada.
 */
export const fetchChartImageObjectUrl = async (
    projectId: number,
    imageType: ProjectChartImageType
): Promise<string | null> => {
    try {
        const response = await apiClient.get(`/ProjectChartImages/${projectId}/${imageType}`, {
            responseType: 'blob'
        });
        return URL.createObjectURL(response.data);
    } catch (error) {
        // Un 404 solo significa que la capa no está cargada todavía.
        return null;
    }
};

/** Carga o reemplaza una imagen de contexto. */
export const uploadChartImage = async (
    projectId: number,
    imageType: ProjectChartImageType,
    file: File
): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    await apiClient.post(`/ProjectChartImages/${projectId}/${imageType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

/**
 * Vista previa de la importación de coordenadas desde un KMZ.
 * No escribe nada: informa qué torres quedarían emparejadas y qué queda fuera.
 */
export const previewTowerKmz = async (projectId: number, file: File): Promise<KmzImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/Towers/kmz/preview/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
};

/** Importa las coordenadas del KMZ y las guarda en las torres del proyecto. */
export const importTowerKmz = async (projectId: number, file: File): Promise<KmzImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/Towers/kmz/import/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
};

/** Elimina una imagen de contexto. */
export const deleteChartImage = async (projectId: number, imageType: ProjectChartImageType): Promise<void> => {
    await apiClient.delete(`/ProjectChartImages/${projectId}/${imageType}`);
};

/**
 * Descarga las coordenadas cargadas de las torres como archivo .kmz.
 *
 * El backend lo reconstruye desde la latitud y longitud de cada torre, en el mismo formato que
 * espera la importacion, de modo que el archivo descargado se puede volver a importar.
 */
export const downloadTowerKmz = async (projectId: number): Promise<void> => {
    const response = await apiClient.get<Blob>(`/Towers/kmz/export/${projectId}`, { responseType: 'blob' });
    saveResponseAsFile(response, 'Coordenadas-torres.kmz');
};

/** Indica cuántas torres del proyecto tienen coordenada, sin generar el diagrama. */
export const getTowerCoordinateStatus = async (projectId: number): Promise<TowerCoordinateStatus> => {
    const response = await apiClient.get(`/Towers/kmz/status/${projectId}`);
    return response.data;
};

/**
 * Borra las coordenadas importadas del proyecto.
 * Las torres no se tocan: el diagrama vuelve a calcular el kilometraje por suma de vanos.
 */
export const clearTowerCoordinates = async (projectId: number): Promise<TowerCoordinateStatus> => {
    const response = await apiClient.delete(`/Towers/kmz/${projectId}`);
    return response.data;
};
